// WbWeightDisplay — Phase C10 Group 3.
//
// The "live readout" capture widget for the centre pane. Two modes:
//
//   1. SSE mode (dev mock-agent ON + a weighbridge id is pinned). Opens a
//      browser EventSource at /api/v1/wb/mock-agent/stream/:enc_id and
//      pumps each 'weight' event into WbTicketContext (setLiveWeight).
//      The Capture button stays disabled until is_stable=true arrives.
//   2. Manual mode (flag off OR no weighbridge picked). A bare number
//      input. Capture is enabled as soon as the value is > 0; the click
//      hands off `method='manual'` so the server tags the wb_tickets
//      column accordingly.
//
// We deliberately keep this purely presentational — it doesn't decide
// gross vs tare. The parent (WbTicketCentrePaneWeight) wraps it for each
// weight type the current weighing_mode requires.
//
// EventSource is closed on unmount, on weighbridgeEncId change, and on
// any error/close to keep the connection lifecycle tight.

import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../components/ui/Button/Button';
import { useWbLiveWeight } from '../../context/WbLiveWeightContext';
import { useWbLiveFeed } from '../../../../hooks/wb/useWbLiveFeed';
import { useWbWeighbridge } from '../../hooks/useWbWeighbridges';
import styles from './WbTicketCentrePane.module.css';

const WEIGHT_TYPE_LABEL: Record<'gross' | 'tare', string> = {
  gross: 'Gross weight',
  tare: 'Tare weight',
};

interface MockAgentWeightPayload {
  weight_kg?: number | string | null;
  is_stable?: boolean | null;
  ts?: string;
  weighbridge_id?: string;
  raw_indicator_data?: string;
}

export interface WbWeightDisplayProps {
  /** The active weighbridge for SSE subscriptions. `null` falls back to manual. */
  weighbridgeEncId: string | null;
  /** Drives the label ("Gross weight" / "Tare weight"). */
  weightType: 'gross' | 'tare';
  /** Fired when the operator confirms a value. */
  onCapture: (kg: number, isStable: boolean, method: 'hardware' | 'manual') => void;
  /** Disables the widget (e.g. waiting on customer pick). */
  disabled?: boolean;
  /** When false, this widget does NOT open its own agent SSE feed — a parent
   *  (the BridgeSelectorRow) owns the single subscription and feeds the shared
   *  live-weight context. The readout/capture still work off that context. */
  feedEnabled?: boolean;
}

const KG_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

export function WbWeightDisplay({
  weighbridgeEncId,
  weightType,
  onCapture,
  disabled = false,
  feedEnabled = true,
}: WbWeightDisplayProps): JSX.Element {
  const {
    liveWeightKg,
    liveWeightStable,
    setLiveWeight,
    setWeightStreamActive,
  } = useWbLiveWeight();

  // Read env at render — vi.stubEnv('VITE_WB_MOCK_AGENT_MODE', 'true') in
  // tests + the Vite build-time flag in dev. Server tests cover the
  // mounting condition; here we just trust the string.
  const mockAgentMode =
    (import.meta.env.VITE_WB_MOCK_AGENT_MODE as string | undefined) === 'true';

  const useSse = mockAgentMode && weighbridgeEncId !== null && !disabled;

  // Phase 3 — the real on-prem agent feed (GET /api/v1/wb/live). Active
  // whenever a weighbridge is pinned and we're NOT in the dev mock-agent
  // mode (the two SSE sources are mutually exclusive so we never open two
  // EventSources). Each weight_update pumps the shared context readout so
  // the capture button + indicator below stay source-agnostic.
  const useAgentFeed = !mockAgentMode && weighbridgeEncId !== null && !disabled;
  // The SSE feed is kept purely for live weight readings. Presence ("agent
  // connected") is the weighbridge's heartbeat-derived `agent_online`, NOT the
  // EventSource transport state — an open pipe to the server doesn't mean a
  // physical agent is streaming.
  useWbLiveFeed({
    enabled: useAgentFeed && feedEnabled,
    onWeightUpdate: (ev) => setLiveWeight(ev.value, ev.stable),
  });
  const { data: weighbridgeData } = useWbWeighbridge(
    useAgentFeed ? (weighbridgeEncId ?? undefined) : undefined,
    { enablePolling: true },
  );
  const agentConnected = weighbridgeData?.weighbridge?.agent_online ?? false;

  // Local state for the manual entry branch. We deliberately don't share
  // this with context.liveWeightKg — that value belongs to the SSE feed.
  const [manualValue, setManualValue] = useState<string>('');
  // Manual-entry escape hatch shown beneath the live agent readout (for
  // when no agent is connected or the operator needs to override).
  const [showManual, setShowManual] = useState<boolean>(false);

  // -- SSE wiring ----------------------------------------------------------
  //
  // Open / close based on (mockAgentMode + weighbridgeEncId). We avoid the
  // hooks-in-conditionals trap by always running the effect — internally
  // it short-circuits when SSE isn't applicable. The 'weight' event is the
  // only one we care about for C10; other custom events are ignored.
  const esRef = useRef<EventSource | null>(null);
  useEffect(() => {
    if (!useSse) {
      // Manual mode — make sure no previous stream is leaking through.
      if (esRef.current !== null) {
        esRef.current.close();
        esRef.current = null;
        setWeightStreamActive(false);
      }
      return;
    }
    // Only attempt the connection when we actually have an id. The
    // useSse guard already excludes null, but we re-check to satisfy the
    // type narrowing.
    if (weighbridgeEncId === null) return;

    const url = `/api/v1/wb/mock-agent/stream/${encodeURIComponent(weighbridgeEncId)}`;
    let es: EventSource;
    try {
      es = new EventSource(url);
    } catch {
      // jsdom doesn't ship EventSource; tests must mock it. If construction
      // fails we silently fall through to the manual branch — the operator
      // can still capture by typing a value.
      return;
    }
    esRef.current = es;

    function handleOpen() {
      setWeightStreamActive(true);
    }
    function handleWeight(ev: MessageEvent) {
      try {
        const data = JSON.parse(ev.data as string) as MockAgentWeightPayload;
        const kg =
          typeof data.weight_kg === 'number'
            ? data.weight_kg
            : typeof data.weight_kg === 'string'
              ? Number(data.weight_kg)
              : null;
        const stable = data.is_stable === true;
        if (kg !== null && Number.isFinite(kg)) {
          setLiveWeight(kg, stable);
        }
      } catch {
        // Malformed payload — drop. The on-prem agent owns format, this
        // dev mock is best-effort.
      }
    }
    function handleError() {
      setWeightStreamActive(false);
    }

    es.addEventListener('open', handleOpen);
    es.addEventListener('weight', handleWeight as EventListener);
    es.addEventListener('error', handleError);

    return () => {
      es.removeEventListener('open', handleOpen);
      es.removeEventListener('weight', handleWeight as EventListener);
      es.removeEventListener('error', handleError);
      es.close();
      if (esRef.current === es) {
        esRef.current = null;
      }
      setWeightStreamActive(false);
    };
  }, [useSse, weighbridgeEncId, setLiveWeight, setWeightStreamActive]);

  // Mirror the agent-feed connection state onto the shared stream flag so
  // the right rail reacts the same way it does for the mock SSE source.
  useEffect(() => {
    if (useAgentFeed) setWeightStreamActive(agentConnected);
  }, [useAgentFeed, agentConnected, setWeightStreamActive]);

  // -- Capture handlers ----------------------------------------------------

  function handleCaptureFromStream() {
    if (liveWeightKg === null || !liveWeightStable) return;
    onCapture(liveWeightKg, liveWeightStable, 'hardware');
  }

  function handleCaptureManual() {
    const parsed = Number(manualValue);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    onCapture(parsed, true, 'manual');
    setManualValue('');
  }

  // -- Render --------------------------------------------------------------

  const label = WEIGHT_TYPE_LABEL[weightType];

  if (useSse) {
    const canCapture =
      !disabled && liveWeightKg !== null && liveWeightStable;
    return (
      <div className={styles.weightWidget} data-testid="wb-weight-display">
        <span className={styles.weightLabel}>{label}</span>
        <div className={styles.weightReadoutRow}>
          <span
            className={`${styles.weightDot} ${
              liveWeightStable ? styles.weightDotStable : styles.weightDotUnstable
            }`}
            aria-hidden
          />
          <span className={styles.weightReadout}>
            {liveWeightKg === null ? '—' : KG_FORMATTER.format(liveWeightKg)}
            <span className={styles.weightUnit}>kg</span>
          </span>
        </div>
        <span className={styles.weightStreamHint}>
          {liveWeightStable ? 'Stable — ready to capture.' : 'Awaiting stable reading…'}
        </span>
        <Button
          variant="primary"
          onClick={handleCaptureFromStream}
          disabled={!canCapture}
          data-testid="wb-weight-capture"
        >
          Capture {weightType}
        </Button>
      </div>
    );
  }

  // -- Real on-prem agent feed (Phase 3) ----------------------------------
  if (useAgentFeed) {
    const canCapture = liveWeightKg !== null && liveWeightStable;
    const manualValidAgent =
      Number(manualValue) > 0 && Number.isFinite(Number(manualValue));
    const hint = !agentConnected
      ? 'No agent connected'
      : liveWeightKg === null
        ? 'Agent connected — awaiting reading'
        : liveWeightStable
          ? 'Stable — ready to capture.'
          : 'Awaiting stable reading…';
    return (
      <div className={styles.weightWidget} data-testid="wb-weight-display">
        <span className={styles.weightLabel}>{label}</span>
        <div className={styles.weightReadoutRow}>
          <span
            className={`${styles.weightDot} ${
              agentConnected && liveWeightStable
                ? styles.weightDotStable
                : styles.weightDotUnstable
            }`}
            aria-hidden
          />
          <span className={styles.weightReadout}>
            {liveWeightKg === null ? '—' : KG_FORMATTER.format(liveWeightKg)}
            <span className={styles.weightUnit}>kg</span>
          </span>
        </div>
        <span
          className={styles.weightStreamHint}
          data-testid="wb-weight-agent-status"
        >
          {hint}
        </span>
        <Button
          variant="primary"
          onClick={handleCaptureFromStream}
          disabled={!canCapture}
          data-testid="wb-weight-capture"
        >
          Capture {weightType}
        </Button>

        <button
          type="button"
          className={styles.manualToggle}
          onClick={() => setShowManual((v) => !v)}
          data-testid="wb-weight-manual-toggle"
        >
          {showManual ? 'Hide manual entry' : 'Enter manually'}
        </button>
        {showManual ? (
          <div className={styles.manualWeightRow}>
            <input
              type="number"
              className={styles.manualWeightInput}
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              step={0.1}
              min={0.1}
              placeholder="0.0"
              aria-label={`${label} value in kilograms`}
              data-testid="wb-weight-manual-input"
            />
            <span className={styles.weightUnit}>kg</span>
            <Button
              variant="secondary"
              onClick={handleCaptureManual}
              disabled={!manualValidAgent}
              data-testid="wb-weight-manual-capture"
            >
              Capture manually
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  // Manual mode.
  const manualValid = Number(manualValue) > 0 && Number.isFinite(Number(manualValue));
  return (
    <div className={styles.weightWidget} data-testid="wb-weight-display">
      <span className={styles.weightLabel}>{label}</span>
      <div className={styles.manualWeightRow}>
        <input
          type="number"
          className={styles.manualWeightInput}
          value={manualValue}
          onChange={(e) => setManualValue(e.target.value)}
          step={0.1}
          min={0.1}
          placeholder="0.0"
          aria-label={`${label} value in kilograms`}
          disabled={disabled}
          data-testid="wb-weight-manual-input"
        />
        <span className={styles.weightUnit}>kg</span>
      </div>
      <Button
        variant="primary"
        onClick={handleCaptureManual}
        disabled={disabled || !manualValid}
        data-testid="wb-weight-capture"
      >
        Capture {weightType}
      </Button>
    </div>
  );
}
